import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import theme from '../theme';
import { TerminalCard, TerminalButton, TerminalTitle, TerminalSubtitle, TerminalText } from './shared/TerminalBox';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const Pricing = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const handleGetStarted = (plan) => {
    navigate('/payment-form', { state: { planType: plan } });
  };

  const plans = [
    {
      name: 'Core Plan',
      price: 97,
      tier: 'CORE',
      description: 'For solo developers, indie builders, and early-stage startups.',
      features: [
        '100,000 API calls/month',
        'Real-time market data (crypto, stocks, FX)',
        'Encryption & hashing endpoints (AES, RSA, SHA256, HMAC)',
        'BTC/ETH address validator',
        'VPN access: 1 device (AES-256 secured tunnel)',
        '3 API keys',
        '99.9% uptime SLA',
        'Basic analytics dashboard',
        'Email support within 48 hours'
      ],
      popular: false
    },
    {
      name: 'Growth Plan',
      price: 497,
      tier: 'GROWTH',
      description: 'For scaling startups, tool builders, and Web3 teams.',
      features: [
        '500,000 API calls/month',
        'Dedicated AI-backed request simulation (for dev testing)',
        'Secure signature generation + verification APIs',
        'Priority refresh rates for market feeds (5s intervals)',
        'Key rotation & secret management API',
        'Branded invoice generator module (for crypto billing simulation)',
        'VPN access: 5 devices + optional static IP',
        'Priority support with 24-hour response SLA'
      ],
      popular: false
    },
    {
      name: 'Enterprise Plan',
      price: 997,
      tier: 'ENTERPRISE',
      description: 'For agencies, fintech stacks, and infrastructure-level use.',
      features: [
        '1,000,000 API calls/month',
        'Access to all cryptographic utilities (key derivation, random generators)',
        'Encrypted file storage endpoint (AES256-GCM upload/download)',
        'White-label dashboard & API branding',
        'Custom invoice system (dynamic metadata + address generator)',
        'VPN: 15 devices, dedicated tunnel, regional server locking',
        'Private Slack channel + 1:1 onboarding',
        'Dedicated engineering support (by request)',
        'Custom plan negotiation available for high volume teams'
      ],
      popular: true
    },
    {
      name: 'Ultimate Plan',
      price: 2997,
      tier: 'ULTIMATE',
      description: 'For enterprise teams requiring unlimited scale and premium support.',
      features: [
        'Unlimited API calls',
        'Full access to all cryptographic utilities',
        'Encrypted file storage endpoint (AES256-GCM upload/download)',
        'White-label dashboard & API branding',
        'Custom invoice system (dynamic metadata + address generator)',
        'Advanced analytics & reporting',
        'VPN: Unlimited devices, dedicated infrastructure',
        'Private Slack channel + dedicated account manager',
        '24/7 premium engineering support',
        'Custom integrations & development',
        'Priority feature requests',
        'SLA with 99.99% uptime guarantee'
      ],
      popular: false
    },
    {
      name: 'Ulta Mega Plan',
      price: 4997,
      tier: 'ULTA MEGA',
      description: 'For teams that need the highest tier of access, support, and infrastructure.',
      features: [
        'Unlimited API calls',
        'Full access to all cryptographic utilities',
        'Encrypted file storage endpoint (AES256-GCM upload/download)',
        'White-label dashboard & API branding',
        'Custom invoice system (dynamic metadata + address generator)',
        'Advanced analytics & reporting',
        'VPN: Unlimited devices, dedicated infrastructure',
        'Private Slack channel + dedicated account manager',
        '24/7 premium engineering support',
        'Custom integrations & development',
        'Priority feature requests',
        'SLA with 99.99% uptime guarantee',
        'Dedicated infrastructure & on-call engineering'
      ],
      popular: false
    }
  ];

  return (
    <Container>
      <Header>
        <Title>Pricing Plans</Title>
        <Subtitle>
          Choose the perfect plan for your development needs. All plans include enterprise-grade security and 99.9% uptime SLA.
        </Subtitle>
      </Header>

      <PlansContainer>
        {plans.map((plan, index) => (
          <PlanCard key={index} popular={plan.popular}>
            {plan.popular && <PopularBadge>[ RECOMMENDED ]</PopularBadge>}
            <PlanHeader>
              <PlanTier>{plan.tier}</PlanTier>
              <PlanName>{plan.name}</PlanName>
              <PlanPrice>
                <Currency>$</Currency>
                {plan.price}
                <Period>/month</Period>
              </PlanPrice>
              <PlanDescription>{plan.description}</PlanDescription>
            </PlanHeader>
            
            <PlanFeatures>
              {plan.features.map((feature, featureIndex) => (
                <FeatureItem key={featureIndex}>
                  <FeatureMarker>></FeatureMarker>
                  {feature}
                </FeatureItem>
              ))}
            </PlanFeatures>
            
            <PlanButton 
              onClick={() => handleGetStarted(plan.name.toLowerCase().replace(/ plan/g, '').replace(/\s+/g, ''))}
              popular={plan.popular}
            >
              {plan.popular ? '[ SELECT PLAN ]' : 'Get Started'}
            </PlanButton>
          </PlanCard>
        ))}
      </PlansContainer>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem;
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled(TerminalTitle)`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
  
  &::after {
    content: ' <';
    color: ${theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(TerminalText)`
  font-size: 1.1rem;
  color: ${theme.colors.textSecondary};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.8;
  font-family: ${theme.fonts.mono};
`;

const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled(TerminalCard)`
  position: relative;
  text-align: left;
  transition: all ${theme.transitions.normal};
  border: ${props => props.popular ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`};
  box-shadow: ${props => props.popular ? theme.shadows.glowStrong : theme.shadows.card};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.glowStrong};
    border-color: ${theme.colors.primaryLight};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.bg};
  color: ${theme.colors.primary};
  padding: 0.5rem 1.5rem;
  border: 1px solid ${theme.colors.borderBright};
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  box-shadow: ${theme.shadows.glow};
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border};
`;

const PlanTier = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
  color: ${theme.colors.primary};
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 1rem;
  
  &::before {
    content: '[ ';
  }
  
  &::after {
    content: ' ]';
  }
`;

const PlanName = styled.h3`
  font-family: ${theme.fonts.mono};
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const PlanPrice = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 3rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  text-shadow: ${theme.shadows.glow};
`;

const Currency = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.textSecondary};
`;

const Period = styled.span`
  font-family: ${theme.fonts.mono};
  font-size: 1rem;
  font-weight: 500;
  color: ${theme.colors.textMuted};
  margin-left: 0.5rem;
`;

const PlanDescription = styled(TerminalText)`
  color: ${theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: center;
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
  min-height: 300px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  color: ${theme.colors.text};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  line-height: 1.6;
  border-bottom: 1px solid ${theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const FeatureMarker = styled.span`
  color: ${theme.colors.primary};
  font-weight: 700;
  flex-shrink: 0;
`;

const PlanButton = styled(TerminalButton)`
  width: 100%;
  padding: 1rem 2rem;
  font-size: 1rem;
  margin-top: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 700;
  border: ${props => props.popular ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.borderBright}`};
  box-shadow: ${props => props.popular ? theme.shadows.glow : 'none'};
  
  &:hover {
    box-shadow: ${theme.shadows.glowStrong};
    border-color: ${theme.colors.primaryLight};
  }
`;

export default Pricing;
